import classNames from 'classnames';
import React, { useState } from 'react';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import { MenuItem } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import SVGIcon from '../../components/SVGIcon';
import arrowRightIcon from '../../icons/non-prefilled/16x16/arrowRight.svg';
import memberIcon from '../../icons/non-prefilled/16x16/member.svg';
import overflowMenuIcon from '../../icons/non-prefilled/16x16/overflowMenu.svg';
import useNavigate from '../../utils/router/useNavigate';
import userPath from '../../utils/url/userPath';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import styles from './VersionChangesWallAuthorCard.module.scss';
import formatHistoryDate from './formatHistoryDate';
import historyPath from '../../utils/url/historyPath';
import PopOver from '../../components/deprecated/PopOver';
import PopOverContainer, {
  PopOverContainerArrow,
} from '../../components/deprecated/PopOverContainer';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

export const AuthorCardSizes = {
  heights: {
    compact: 40,
    normal: 70,
  },
  normalWidth: parseInt(styles.normalWidth, 10),
};

const USER_FRAGMENT = gql(`
  fragment VersionChangesWallAuthorCard_user on PublicUserFields {
    id
    ...MagneticWallItemAvatar_user
    ...userPath_user
  }
`);

const HISTORY_FRAGMENT = gql(`
  fragment VersionChangesWallAuthorCard_history on SlimHistory {
    id
    insertedAt
    versionFrom
    versionTo
  }
`);

const POST_FRAGMENT = gql(`
  fragment VersionChangesWallAuthorCard_post on Post {
    id
    ...historyPath_post
  }
`);

interface AuthorCardProps {
  author: DocumentType<typeof USER_FRAGMENT>;
  history: DocumentType<typeof HISTORY_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  focused: boolean;
  currentVersionFrom: number;
  currentVersionTo: number;
  asPopOver: boolean;
  onClick: () => void;
  initialIsPopOverOpen?: boolean;
}

const VersionChangesWallAuthorCard = ({
  author,
  history,
  post,
  focused,
  currentVersionFrom,
  currentVersionTo,
  asPopOver,
  onClick,
  initialIsPopOverOpen,
}: AuthorCardProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const isAtCurrentVersion =
    history.versionFrom === currentVersionFrom &&
    history.versionTo === currentVersionTo;

  const authorDate = (
    <>
      <div className={styles.authorName}>{author.name}</div>
      <div className={styles.date}>{formatHistoryDate(history.insertedAt)}</div>
    </>
  );

  const card = (
    <div className={styles.card}>
      {asPopOver ? (
        <div className={styles.content}>{authorDate}</div>
      ) : (
        <Button
          className={styles.content}
          onClick={onClick}
          testId="versionChangesWallAuthorCardButton"
        >
          <div className={styles.avatar}>
            <MagneticWallItemAvatar user={author} />
          </div>
          {authorDate}
        </Button>
      )}
      <div className={styles.menu}>
        <OverflowMenu
          open={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          button={
            <IconButton aria-label="Actions" className={styles.button}>
              <SVGIcon size="small">{overflowMenuIcon}</SVGIcon>
            </IconButton>
          }
        >
          <MenuItem
            disabled={isAtCurrentVersion}
            icon={arrowRightIcon}
            tooltip={isAtCurrentVersion ? 'You already on this version' : null}
            onClick={() => {
              navigate(
                historyPath(post, history.versionFrom, history.versionTo),
              );
            }}
          >
            Go to version
          </MenuItem>
          <MenuItem
            icon={memberIcon}
            onClick={() => {
              navigate(userPath(author));
            }}
          >
            Open profile
          </MenuItem>
        </OverflowMenu>
      </div>
    </div>
  );

  const [isPopOverOpen, setIsPopOverOpen] = useState(
    initialIsPopOverOpen ?? false,
  );
  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.isFocused]: focused,
        [styles.asPopOver]: asPopOver,
        [styles.isMenuOpen]: isMenuOpen,
      })}
    >
      {asPopOver ? (
        // @ts-expect-error
        <PopOver
          placement="left"
          trigger="click"
          // @ts-expect-error
          arrow={<PopOverContainerArrow small />}
          modifiers={[
            { name: 'offset', options: { offset: [10, 8] } },
            { name: 'preventOverflow', enabled: false },
          ]}
          open={isPopOverOpen}
          onOpenChange={setIsPopOverOpen}
          preventCloseOnContentClick
          popOver={
            <div className={styles.popOver}>
              <PopOverContainer noPadding noMargin width="auto">
                {card}
              </PopOverContainer>
            </div>
          }
        >
          <Button onClick={onClick}>
            <MagneticWallItemAvatar user={author} />
          </Button>
        </PopOver>
      ) : (
        card
      )}
    </div>
  );
};

export default VersionChangesWallAuthorCard;
